import React, { useState, useEffect, Suspense } from 'react';
import { lazyWithReload } from './utils/lazyWithReload';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthWrapper } from './components/auth/AuthWrapper';
import { DashboardLayout } from './components/Layout/DashboardLayout';
import { DashboardModule } from './components/DashboardModule';
import { ToastContainer } from './components/ToastNotification';
import { ToastProvider, useToast, HelpContextProvider } from './contexts/ToastContext';
import { LoadingSpinner } from './components/auth/LoadingSpinner';
import { ContextualLoader } from './components/ui/ContextualLoader';
import { usePreloadResources, useSmartPreloading } from './hooks/usePreloading';
import { useAuth } from './hooks/useAuth';
import { DebugService } from './services/debugService';
import './styles/enhanced-ui.css';
import { SessionWorkspaceOverlay } from './components/crm/SessionWorkspaceOverlay';

// Lazy load components for code splitting
const GeneratedCertificatesModule = lazyWithReload(() =>
  import('./components/generatedCertificatesModule').then((module) => ({ default: module.GeneratedCertificatesModule }))
);
const ClientCRMModule = lazyWithReload(() => import('./components/crm/ClientCRMModule').then(module => ({ default: module.ClientCRMModule })));
const RemindersModule = lazyWithReload(() => import('./components/reminders/RemindersModule').then(module => ({ default: module.RemindersModule })));
const ReviewsModule = lazyWithReload(() => import('./components/reviews/ReviewsModule').then(module => ({ default: module.ReviewsModule })));
const BookingsModule = lazyWithReload(() =>
  import('./components/BookingsModule').then((module) => ({ default: module.BookingsModule }))
);
const SuperAdminPage = lazyWithReload(() =>
  import('./components/superadmin/SuperAdminPage')
    .then((module) => ({ default: module.SuperAdminPage }))
    .catch((err) => {
      console.error('Failed to load SuperAdminPage chunk:', err);
      return {
        default: function SuperAdminChunkError() {
          return (
            <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 p-8 text-center text-slate-700">
              <p className="max-w-md text-sm">
                The Super Admin screen could not be loaded (network interruption or dev server hiccup). Try reloading the page.
              </p>
              <button
                type="button"
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800"
                onClick={() => window.location.reload()}
              >
                Reload page
              </button>
            </div>
          );
        },
      };
    })
);
const AdminPage = lazyWithReload(() => import('./components/admin/AdminPage').then(module => ({ default: module.AdminPage })));
const FAQPage = lazyWithReload(() => import('./components/FAQPage').then(module => ({ default: module.FAQPage })));
const TasksRemindersPage = lazyWithReload(() => import('./pages/TasksRemindersPage').then(module => ({ default: module.default })));
const FinancialReportsPage = lazyWithReload(() => import('./pages/FinancialReportsPage').then(module => ({ default: module.FinancialReportsPage })));
const PublicFormPage = lazyWithReload(() => import('./pages/PublicFormPage').then(module => ({ default: module.default }))); // Check-in form page
const GDPRConsentPage = lazyWithReload(() => import('./pages/GDPRConsentPage').then(module => ({ default: module.default })));
const QuoteAcceptancePage = lazyWithReload(() => import('./components/quotes/QuoteAcceptancePage').then(module => ({ default: module.QuoteAcceptancePage })));
const QuoteSuccessPage = lazyWithReload(() => import('./components/quotes/QuoteSuccessPage').then(module => ({ default: module.QuoteSuccessPage })));
const QuotesModule = lazyWithReload(() => import('./components/quotes/QuotesModule').then(module => ({ default: module.QuotesModule })));
const BulkEmailPage = lazyWithReload(() => import('./pages/BulkEmailPage').then(module => ({ default: module.default })));
const OpsDayPlannerPage = lazyWithReload(() => import('./pages/OpsDayPlannerPage').then(module => ({ default: module.OpsDayPlannerPage })));
const ProcessNumbersModule = lazyWithReload(() =>
  import('./components/processNumbers/ProcessNumbersModule').then((module) => ({
    default: module.ProcessNumbersModule,
  }))
);
const InstructorAssignmentsPage = lazyWithReload(() => import('./pages/InstructorAssignmentsPage').then(module => ({ default: module.InstructorAssignmentsPage })));
const InterpreterAssignmentsPage = lazyWithReload(() => import('./pages/InterpreterAssignmentsPage').then(module => ({ default: module.InterpreterAssignmentsPage })));
const BookingPortalPage = lazyWithReload(() => import('./pages/BookingPortalPage').then(module => ({ default: module.BookingPortalPage })));
const ClientPortalPage = lazyWithReload(() => import('./pages/ClientPortalPage').then(module => ({ default: module.default })));
const SandboxPortalPage = lazyWithReload(() => import('./pages/SandboxPortalPage').then(module => ({ default: module.SandboxPortalPage })));
const BrochurePage = lazyWithReload(() => import('./pages/BrochurePage').then(module => ({ default: module.default })));
const ThankYouPage = lazyWithReload(() => import('./pages/ThankYouPage').then(module => ({ default: module.ThankYouPage })));
const TakeTestPage = lazyWithReload(() => import('./pages/TakeTestPage').then(module => ({ default: module.default })));
const VerifyCertificatePage = lazyWithReload(() => import('./pages/VerifyCertificatePage').then(module => ({ default: module.VerifyCertificatePage })));
const OwnerEfficiencyPage = lazyWithReload(() => import('./pages/OwnerEfficiencyPage'));
import type { AppView } from './types/views';
import { APP_VIEWS } from './types/views';
type View = AppView;

// Component that uses the toast hook
function AppContent() {
  // Initialize activeView from localStorage or default to 'dashboard'
  const [activeView, setActiveView] = useState<View>(() => {
    const savedView = localStorage.getItem('activeView') as View;
    return savedView && APP_VIEWS.includes(savedView) 
      ? savedView 
      : 'dashboard';
  });
  const [navigationContext, setNavigationContext] = useState<Record<string, unknown> | null>(null);
  const [previousViewState, setPreviousViewState] = useState<{
    view: View;
    context: Record<string, unknown> | null;
  } | null>(null);
  const [sessionOverlayId, setSessionOverlayId] = useState<string | null>(null);
  const { toasts, dismissToast } = useToast();
  const { user } = useAuth();

  // Initialize preloading
  usePreloadResources();
  const { preloadBasedOnView } = useSmartPreloading();

  const openSessionOverlay = (sessionId: string) => {
    if (sessionId) setSessionOverlayId(sessionId);
  };

  const closeSessionOverlay = () => {
    setSessionOverlayId(null);
  };

  const handleViewChange = (view: View, context?: Record<string, unknown>) => {
    if (view !== activeView) {
      setPreviousViewState({ view: activeView, context: navigationContext });
    }
    setActiveView(view);
    setNavigationContext(context || null);
    
    // Preload resources for the new view
    preloadBasedOnView(view);
    
    // Save the current view to localStorage so it persists on refresh
    localStorage.setItem('activeView', view);
    
    // Update URL to reflect current view and context
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('view', view);
      
      // Clear any existing context params
      url.searchParams.delete('context');
      url.searchParams.delete('customerId');
      url.searchParams.delete('bookingId');
      url.searchParams.delete('sessionId');
      url.searchParams.delete('filter');
      
      // Add specific context parameters for better URL readability
      if (context) {
        if (context.selectedCustomerId) {
          url.searchParams.set('customerId', context.selectedCustomerId as string);
        }
        if (context.bookingId) {
          url.searchParams.set('bookingId', context.bookingId as string);
        }
        if (context.sessionId) {
          url.searchParams.set('sessionId', context.sessionId as string);
        }
        if (context.filter) {
          url.searchParams.set('filter', context.filter as string);
        }
        if (context.instructorId) {
          url.searchParams.set('instructorId', context.instructorId as string);
        }
        if (context.activeTab) {
          url.searchParams.set('tab', context.activeTab as string);
        }
        
        // Also store full context as JSON for complex scenarios
        url.searchParams.set('context', JSON.stringify(context));
      }
      
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      console.warn('Failed to update URL:', e);
    }
  };

  // Handle URL parameters on component mount and browser back/forward
  useEffect(() => {
    const handleUrlChange = () => {
      try {
        if (DebugService && typeof DebugService.agentLog === 'function') {
          DebugService.agentLog({
            location: 'App.tsx:handleUrlChange',
            message: 'URL change detected',
            data: { href: window.location.href, pathname: window.location.pathname },
          });
        }
      } catch (error) {
        // Silently fail if agentLog is not available
      }

      const url = new URL(window.location.href);
      const viewParam = url.searchParams.get('view') as View;
      const contextParam = url.searchParams.get('context');
      const customerIdParam = url.searchParams.get('customerId');
      const bookingIdParam = url.searchParams.get('bookingId');
      const sessionIdParam = url.searchParams.get('sessionId');
      const filterParam = url.searchParams.get('filter');
      const instructorIdParam = url.searchParams.get('instructorId');
      const tabParam = url.searchParams.get('tab');
      
      if (viewParam && APP_VIEWS.includes(viewParam)) {
        try {
          if (DebugService && typeof DebugService.agentLog === 'function') {
            DebugService.agentLog({
              location: 'App.tsx:handleUrlChange',
              message: 'Setting active view from URL',
              data: { viewParam, currentActiveView: activeView },
            });
          }
        } catch (error) {
          // Silently fail if agentLog is not available
        }

        setActiveView(viewParam);
        setPreviousViewState(null);
        localStorage.setItem('activeView', viewParam);
        
        // Build context from URL parameters
        const context: Record<string, unknown> = {};
        
        if (customerIdParam) {
          context.selectedCustomerId = customerIdParam;
        }
        if (bookingIdParam) {
          context.bookingId = bookingIdParam;
        }
        if (sessionIdParam) {
          context.sessionId = sessionIdParam;
        }
        if (filterParam) {
          context.filter = filterParam;
        }
        if (instructorIdParam) {
          context.instructorId = instructorIdParam;
        }
        if (tabParam) {
          context.activeTab = tabParam;
        }
        
        // Merge with context JSON so filterDate and other keys from "Open booking" links are preserved
        if (contextParam) {
          try {
            const parsed = JSON.parse(decodeURIComponent(contextParam)) as Record<string, unknown>;
            Object.assign(context, parsed);
          } catch (e) {
            try {
              const parsed = JSON.parse(contextParam) as Record<string, unknown>;
              Object.assign(context, parsed);
            } catch (e2) {
              console.warn('Failed to parse context from URL:', e2);
            }
          }
        }
        
        if (Object.keys(context).length > 0) {
          setNavigationContext(context);
        }
      } else {
        // If no URL parameter, check localStorage for saved view
        const savedView = localStorage.getItem('activeView') as View;
        if (savedView && ['dashboard', 'certificates', 'clients', 'reminders', 'reviews', 'bookings', 'quotes', 'superadmin', 'admin', 'faq', 'tasks', 'financial', 'emails', 'ops', 'process-numbers'].includes(savedView)) {
          setActiveView(savedView);
          setPreviousViewState(null);
          // Update URL to reflect the saved view
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('view', savedView);
            window.history.replaceState({}, '', url.toString());
          } catch (e) {
            console.warn('Failed to update URL with saved view:', e);
          }
        }
      }
    };

    // Handle initial load
    handleUrlChange();

    // Listen for browser back/forward navigation
    window.addEventListener('popstate', handleUrlChange);
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  const handleNavigationHandled = () => {
    setNavigationContext(null);
    // Clear context from URL when navigation is handled, but preserve external certificate parameters
    try {
      const url = new URL(window.location.href);
      const addExternalCert = url.searchParams.get('addExternalCert');
      const bookingId = url.searchParams.get('bookingId');
      
      url.searchParams.delete('context');
      url.searchParams.delete('customerId');
      url.searchParams.delete('filter');
      url.searchParams.delete('instructorId');
      url.searchParams.delete('tab');
      url.searchParams.delete('sessionId');
      
      // Preserve external certificate parameters
      if (addExternalCert) url.searchParams.set('addExternalCert', addExternalCert);
      if (bookingId) url.searchParams.set('bookingId', bookingId);
      
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      console.warn('Failed to clear URL parameters:', e);
    }
  };

  const renderViewContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <DashboardModule 
            onViewChange={handleViewChange}
            onOpenSession={openSessionOverlay}
            onNavigateToClient={(customerId) =>
              handleViewChange('clients', { selectedCustomerId: customerId })
            }
          />
        );
      case 'certificates':
        return (
          <GeneratedCertificatesModule />
        );
      case 'clients':
        return (
          <ClientCRMModule 
            navigationContext={navigationContext as any}
            onNavigationHandled={handleNavigationHandled}
            onOpenSession={openSessionOverlay}
          />
        );
      case 'reminders':
        return (
          <RemindersModule 
            navigationContext={navigationContext as any}
            onNavigationHandled={handleNavigationHandled}
          />
        );
      case 'reviews':
        return (
          <ReviewsModule 
            navigationContext={navigationContext as any}
            onNavigationHandled={handleNavigationHandled}
          />
        );
      case 'bookings':
        return (
          <BookingsModule
            navigationContext={navigationContext as any}
            onNavigationHandled={handleNavigationHandled}
            onNavigateToClient={(customerId) =>
              handleViewChange('clients', { selectedCustomerId: customerId })
            }
            onViewChange={(view, context) => handleViewChange(view, context)}
            canGoBack={!!previousViewState}
            onGoBack={() => {
              if (!previousViewState) return;
              const prev = previousViewState;
              setPreviousViewState(null);
              handleViewChange(prev.view, prev.context || undefined);
            }}
          />
        );
      case 'quotes':
        return (
          <QuotesModule
            onViewChange={handleViewChange}
            navigationContext={navigationContext}
            onNavigationHandled={handleNavigationHandled}
          />
        );
      case 'admin':
        return (
          <AdminPage
            initialTab={
              navigationContext?.activeTab as
                | 'instructors'
                | 'interpreters'
                | 'emails'
                | 'email-translator'
                | 'bugs'
                | 'reports'
                | 'courses'
                | 'languages'
                | 'providers'
                | 'training-locations'
                | undefined
            }
          />
        );
      case 'superadmin':
        return <SuperAdminPage initialTab={navigationContext?.activeTab as string | undefined} />;
      case 'faq':
        return <FAQPage />;
      case 'tasks':
        return (
          <TasksRemindersPage
            onViewChange={handleViewChange as (view: string, context?: any) => void}
            navigationContext={navigationContext as { filter?: 'all' | 'pending' | 'completed' | 'today' | 'overdue' } | null}
          />
        );
      case 'financial':
        return <FinancialReportsPage />;
      case 'emails':
        return <BulkEmailPage />;
      case 'ops':
        return <OpsDayPlannerPage onViewChange={handleViewChange} />;
      case 'process-numbers':
        return (
          <ProcessNumbersModule
            navigationContext={navigationContext as { activeTab?: 'process-numbers' | 'safe-pass-cards'; sessionId?: string }}
            onNavigationHandled={handleNavigationHandled}
          />
        );
      case 'owner-analytics':
        return (
          <OwnerEfficiencyPage
            onViewChange={handleViewChange}
            onOpenSession={openSessionOverlay}
          />
        );
      default:
        return (
          <DashboardModule 
            onViewChange={handleViewChange}
            onOpenSession={openSessionOverlay}
            onNavigateToClient={(customerId) => 
              handleViewChange('clients', { selectedCustomerId: customerId })
            }
          />
        );
    }
  };

  return (
    <Router>
      <div className="min-h-screen">
        <Routes>
          <Route path="/public-form/:bookingId?" element={
            <Suspense fallback={<LoadingSpinner />}>
              <PublicFormPage />
            </Suspense>
          } />
          <Route path="/gdpr-consent" element={
            <Suspense fallback={<LoadingSpinner />}>
              <GDPRConsentPage />
            </Suspense>
          } />
          <Route path="/accept-quote/:quoteNumber" element={
            <Suspense fallback={<LoadingSpinner />}>
              <QuoteAcceptancePage />
            </Suspense>
          } />
          <Route path="/quote-accepted" element={
            <Suspense fallback={<LoadingSpinner />}>
              <QuoteSuccessPage />
            </Suspense>
          } />
          <Route path="/booking-portal/:token" element={
            <Suspense fallback={<LoadingSpinner />}>
              <BookingPortalPage />
            </Suspense>
          } />
          <Route path="/take-test/:token" element={
            <Suspense fallback={<LoadingSpinner />}>
              <TakeTestPage />
            </Suspense>
          } />
          {/* Token-based client portal (public) */}
          <Route path="/client-portal/:token" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ClientPortalPage />
            </Suspense>
          } />
          {/* Authenticated client portal */}
          <Route path="/client-portal" element={
            <AuthWrapper>
              <Suspense fallback={<LoadingSpinner />}>
                <ClientPortalPage />
              </Suspense>
            </AuthWrapper>
          } />
          {/* Sandbox portal (no authentication required) */}
          <Route path="/sandbox-portal" element={
            <Suspense fallback={<LoadingSpinner />}>
              <SandboxPortalPage />
            </Suspense>
          } />
          <Route path="/instructor-assignments" element={
            <AuthWrapper>
              <Suspense fallback={<LoadingSpinner />}>
                <InstructorAssignmentsPage />
              </Suspense>
            </AuthWrapper>
          } />
          <Route path="/interpreter-assignments" element={
            <AuthWrapper>
              <Suspense fallback={<LoadingSpinner />}>
                <InterpreterAssignmentsPage />
              </Suspense>
            </AuthWrapper>
          } />
          <Route path="/brochure" element={
            <Suspense fallback={<LoadingSpinner />}>
              <BrochurePage />
            </Suspense>
          } />
          <Route path="/thank-you" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ThankYouPage />
            </Suspense>
          } />
          <Route path="/verify/certificate/:certNumber" element={
            <Suspense fallback={<LoadingSpinner />}>
              <VerifyCertificatePage />
            </Suspense>
          } />
          
            <Route path="/*" element={
              <AuthWrapper>
                <DashboardLayout 
                  activeView={activeView} 
                  onViewChange={handleViewChange}
                  canGoBack={!!previousViewState}
                  onGoBack={() => {
                    if (!previousViewState) return;
                    const prev = previousViewState;
                    setPreviousViewState(null);
                    handleViewChange(prev.view, prev.context || undefined);
                  }}
                >
                  <Suspense fallback={<ContextualLoader view={activeView} />}>
                    {renderViewContent()}
                  </Suspense>
                </DashboardLayout>
              </AuthWrapper>
            } />
        </Routes>

        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        {sessionOverlayId && (
          <SessionWorkspaceOverlay
            sessionId={sessionOverlayId}
            onClose={closeSessionOverlay}
            onNavigateToClient={(customerId) => {
              closeSessionOverlay();
              handleViewChange('clients', {
                selectedCustomerId: customerId,
                returnTo: { view: activeView, context: navigationContext, sessionId: sessionOverlayId }
              });
            }}
          />
        )}
      </div>
    </Router>
  );
}

function App() {
  return (
    <ToastProvider>
      <HelpContextProvider>
        <AppContent />
      </HelpContextProvider>
    </ToastProvider>
  );
}

export default App;